import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getErrors } from '../../api/client';
import { formatRelativeTime, formatNumber } from '../../utils/formatters';
import { AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import clsx from 'clsx';

interface ErrorListProps {
  onSelect: (errorId: string) => void;
}

export default function ErrorList({ onSelect }: ErrorListProps) {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['errors', page, statusFilter],
    queryFn: () =>
      getErrors({
        page,
        pageSize: 20,
        status: statusFilter === 'all' ? undefined : statusFilter,
      }),
  });

  const errors = data?.data || [];
  const totalPages = data?.totalPages || 1;

  const statusFilters = [
    { label: 'All', value: 'all' },
    { label: 'Open', value: 'open' },
    { label: 'Resolved', value: 'resolved' },
    { label: 'Ignored', value: 'ignored' },
  ];

  return (
    <div className="space-y-4">
      {/* Status Filter */}
      <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 w-fit">
        {statusFilters.map((f) => (
          <button
            key={f.value}
            onClick={() => {
              setStatusFilter(f.value);
              setPage(1);
            }}
            className={clsx(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              statusFilter === f.value
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700',
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="card flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="table-header">Error</th>
                <th className="table-header">Type</th>
                <th className="table-header text-right">Count</th>
                <th className="table-header text-right">Sessions</th>
                <th className="table-header">Status</th>
                <th className="table-header">Last Seen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {errors.map((error) => (
                <tr
                  key={error.id || error.errorGroupHash}
                  onClick={() => onSelect(error.id || error.errorGroupHash || '')}
                  className="cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <td className="table-cell max-w-md">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
                      <span className="truncate font-medium">{error.message}</span>
                    </div>
                  </td>
                  <td className="table-cell">
                    <span className="badge bg-gray-100 text-gray-600">{error.type || error.errorType || 'Error'}</span>
                  </td>
                  <td className="table-cell text-right font-medium">
                    {formatNumber(error.count ?? error.occurrences ?? 0)}
                  </td>
                  <td className="table-cell text-right text-gray-500">
                    {formatNumber(error.sessionsAffected ?? error.affectedSessions ?? 0)}
                  </td>
                  <td className="table-cell">
                    <span
                      className={clsx(
                        'badge',
                        (error.status || 'open') === 'open' && 'bg-red-100 text-red-800',
                        error.status === 'resolved' && 'bg-green-100 text-green-800',
                        error.status === 'ignored' && 'bg-gray-100 text-gray-800',
                      )}
                    >
                      {error.status || 'open'}
                    </span>
                  </td>
                  <td className="table-cell text-gray-500">
                    {formatRelativeTime(error.lastSeen)}
                  </td>
                </tr>
              ))}
              {errors.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-500">
                    No errors found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page <= 1}
            className="btn-secondary p-2 disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages}
            className="btn-secondary p-2 disabled:opacity-50"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
